import { CommonModule } from '@angular/common';
import {
    Component,
    EventEmitter,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { HeaderNotificationItem } from 'src/app/shared/models/notification.interface';
import {
    NotificationListPaginationState,
    NotificationWsService,
} from './notification-ws.service';

export type NotificationFilterTab = 'all' | 'unread';

@Component({
    selector: 'app-notification',
    standalone: true,
    imports: [CommonModule, TranslatePipe],
    templateUrl: './notification.component.html',
    styleUrls: ['./notification.component.scss'],
})
export class NotificationComponent implements OnInit, OnDestroy {
    @Output() panelClick = new EventEmitter<void>();
    @Input() set setPanelEvent(_value: number) {
        this.closePanel();
    }

    panelOpen = false;
    filterTab: NotificationFilterTab = 'all';

    allNotifications: HeaderNotificationItem[] = [];
    listPagination: NotificationListPaginationState = {
        page: 0,
        totalPages: 0,
        size: 10,
        last: true,
        loading: false,
    };
    unreadBadge = 0;

    private readonly destroy$ = new Subject<void>();
    private readonly mobileDrawerMq =
        typeof window !== 'undefined'
            ? window.matchMedia('(max-width: 1199px)')
            : null;

    readonly defaultAvatar = 'assets/images/avatars/avatar-notification.png';

    constructor(
        private notificationWs: NotificationWsService,
        private translate: TranslateService,
    ) {}

    ngOnInit(): void {
        this.notificationWs.notifications$
            .pipe(takeUntil(this.destroy$))
            .subscribe((list) => {
                this.allNotifications = list;
            });
        this.notificationWs.unreadCount$
            .pipe(takeUntil(this.destroy$))
            .subscribe((c) => {
                this.unreadBadge = c;
            });
        this.notificationWs.listPagination$
            .pipe(takeUntil(this.destroy$))
            .subscribe((p) => {
                this.listPagination = p;
            });
    }

    ngOnDestroy(): void {
        this.syncBodyScrollLock();
        this.destroy$.next();
        this.destroy$.complete();
    }

    get filteredList(): HeaderNotificationItem[] {
        if (this.filterTab === 'unread') {
            return this.allNotifications.filter((n) => !n.read);
        }
        return this.allNotifications;
    }

    get visibleNotifications(): HeaderNotificationItem[] {
        return this.filteredList;
    }

    get loadMoreDisabled(): boolean {
        return this.listPagination.last || this.listPagination.loading;
    }

    togglePanel(event: Event): void {
        event.stopPropagation();
        this.panelClick.emit();
        this.panelOpen = !this.panelOpen;
        this.syncBodyScrollLock();
    }

    closePanel(event?: Event): void {
        event?.stopPropagation();
        if (!this.panelOpen) {
            return;
        }
        this.panelOpen = false;
        this.syncBodyScrollLock();
    }

    private isMobileDrawerViewport(): boolean {
        return this.mobileDrawerMq?.matches ?? false;
    }

    /** Prevent page scroll behind the full-height mobile drawer. */
    private syncBodyScrollLock(): void {
        if (typeof document === 'undefined') {
            return;
        }
        document.body.style.overflow =
            this.panelOpen && this.isMobileDrawerViewport() ? 'hidden' : '';
    }

    setFilter(tab: NotificationFilterTab, event: Event): void {
        event.stopPropagation();
        this.filterTab = tab;
    }

    markAllRead(event: Event): void {
        event.stopPropagation();
        this.notificationWs.markAllRead();
    }

    clearAll(event: Event): void {
        event.stopPropagation();
        this.notificationWs.clearAll();
    }

    loadMore(event: Event): void {
        event.stopPropagation();
        if (this.loadMoreDisabled) {
            return;
        }
        const nextPage = this.listPagination.page + 1;
        this.notificationWs.fetchNotificationList(
            nextPage,
            this.notificationWs.pageSize,
            'append',
        );
    }

    /** “Time ago” vs the user’s local clock; `d` must be an absolute instant (UTC-normalized at parse). */
    relativeTime(d: Date): string {
        const now = Date.now();
        const then = d instanceof Date ? d.getTime() : new Date(d).getTime();
        if (Number.isNaN(then)) {
            return this.translate.instant('header.notifications.time.justNow');
        }

        const diffSeconds = Math.max(0, Math.floor((now - then) / 1000));
        if (diffSeconds < 60) {
            return this.translate.instant('header.notifications.time.justNow');
        }
        if (diffSeconds < 3600) {
            const minutes = Math.floor(diffSeconds / 60);
            return this.translate.instant('header.notifications.time.minutesAgo', {
                count: minutes,
            });
        }
        if (diffSeconds < 86400) {
            const hours = Math.floor(diffSeconds / 3600);
            return this.translate.instant('header.notifications.time.hoursAgo', {
                count: hours,
            });
        }
        const days = Math.floor(diffSeconds / 86400);
        return this.translate.instant('header.notifications.time.daysAgo', {
            count: days,
        });
    }

    onItemClick(n: HeaderNotificationItem, event: Event): void {
        event.stopPropagation();
        if (!n.read) {
            this.notificationWs.markOneRead(n.id);
        }
        // if (n.link) {
        //     window.location.href = n.link;
        // }
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        const t = event.target;
        if (!(t instanceof Element)) {
            return;
        }
        if (!t.closest('.header-notification')) {
            this.closePanel();
        }
    }

    @HostListener('window:resize')
    onWindowResize(): void {
        this.syncBodyScrollLock();
    }
}
